import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { Layout } from '@percent/domain/verifications'
import GoodstackLogo from '@percent/product-nomination-form/assets/icons/goodstack-page-logo.svg'
import { NavigationStackContextController } from '@percent/product-nomination-form/context/navigationStack/NavigationStackContextController/NavigationStackContextController'
import { TokenError } from '@percent/product-nomination-form/errors/TokenError'
import {
  usePartnerConfig,
  useServices,
} from '@percent/product-nomination-form/hooks'
import {
  sessionStorageIsAvailable,
  storage,
} from '@percent/product-nomination-form/utils/storage'

import { useQuery } from '@percent/utility'
import { CountriesContext } from './CountriesContext/CountriesContext'
import { HostedForm } from './HostedForm/HostedForm'
import { ValidationStepContextController } from './HostedForm/validationStepContext/validationStepContextController/ValidationStepContextController'

export const HostedFormContainer = () => {
  const { partnerConfig, setPartnerConfig } = usePartnerConfig()
  const [searchParams] = useSearchParams()
  const { id } = useParams()
  const { t } = useTranslation()
  const { nominationService, countriesService } = useServices()

  const {
    data: countriesData,
    isLoading: isCountriesLoading,
    error: countriesError,
  } = useQuery(['countries'], countriesService.getCountries, { retry: false })

  const { isLoading, refetch, error, status } = useQuery(
    ['getPartnerConfiguration', id],
    nominationService.getNomitationInvite,
    { inviteId: id! },
    {
      retry: false,
      enabled: false,
    }
  )

  useEffect(() => {
    const fetchPartnerConfiguration = async () => {
      try {
        const jwtToken = searchParams.get('token')
        if (!jwtToken) {
          throw new TokenError({ message: 'No token given' })
        }

        if (jwtToken && sessionStorageIsAvailable) {
          storage.setItem('jwtToken', jwtToken)
          const response = await refetch()

          if (response.data) {
            setPartnerConfig(response.data)

            const favicon = document.getElementById(
              'favicon'
            ) as HTMLLinkElement
            const description = document.getElementById(
              'description'
            ) as HTMLMetaElement

            if (favicon && document && description) {
              favicon.href = response.data.configuration.logoUrl
              document.title = t('hostedValidation.site.title', {
                partner: response.data.configuration.partner.name,
              })
              description.content = t('hostedValidation.site.contentApply', {
                partnerName: response.data.configuration.partner.name,
              })
            }
          }
        }
      } catch (error) {
        if (error instanceof TokenError) {
          return console.warn(error)
        }
        throw error
      }
    }

    fetchPartnerConfiguration()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout
      logo={partnerConfig?.configuration.logoUrl ?? GoodstackLogo}
      termsLabel={t('hostedValidation.footer.terms')}
      privacyLabel={t('hostedValidation.footer.privacy')}
    >
      <CountriesContext.Provider value={countriesData ?? []}>
        <NavigationStackContextController>
          <ValidationStepContextController>
            <HostedForm
              isLoading={isLoading || isCountriesLoading}
              isError={status === 'error' || !searchParams.get('token')}
              isInternalError={
                (error as AxiosError)?.response?.status === 500 ||
                (countriesError as AxiosError)?.response?.status === 500
              }
              isBlockedCookies={!sessionStorageIsAvailable}
            />
          </ValidationStepContextController>
        </NavigationStackContextController>
      </CountriesContext.Provider>
    </Layout>
  )
}
